/**
 * Games-per-day compliance — the NCAA report.
 *
 * One definition of "how many games a team plays in a day" and "how long
 * between two of a team's games", computed from the print-pack payload that
 * already carries every placed game with its number, time and court.
 *
 * The limits come from the event's schedule setup (event_format_config) with
 * per-division overrides (event_division_format_overrides); this module never
 * invents a default — an unset limit means nothing to report.
 */

import { isOverDailyCap } from '@/lib/dailyCap';

export type ComplianceLimits = {
  max_games_per_day: number | null;
  back_to_back_minutes: number;
  by_division?: Record<string, { max_games_per_day: number | null; back_to_back_minutes: number | null }>;
};

export type DayGame = {
  game_number: number | null;
  bracket_label?: string | null;
  time: string | null;
  court: string | null;
};

export type OverLimitRow = {
  team: string;
  division: string;
  date: string;
  games: number;
  limit: number;
  rows: DayGame[];
};

export type BackToBackRow = {
  team: string;
  division: string;
  date: string;
  gap_minutes: number;
  minimum: number;
  first: DayGame;
  second: DayGame;
};

const minutes = (t: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export const limitsFor = (limits: ComplianceLimits | null | undefined, division: string) => {
  const ov = limits?.by_division?.[division];
  return {
    maxPerDay: ov?.max_games_per_day ?? limits?.max_games_per_day ?? null,
    backToBack: ov?.back_to_back_minutes ?? limits?.back_to_back_minutes ?? 0,
  };
};

type Appearance = {
  team: string;
  division: string;
  date: string;
  start: number;
  end: number;
  game: DayGame;
};

function appearances(games: any[]): Appearance[] {
  const out: Appearance[] = [];
  for (const g of games ?? []) {
    if (!g?.scheduled_date || !g?.scheduled_start_time) continue;
    if (String(g.game_status) === 'cancelled') continue;
    const start = minutes(g.scheduled_start_time);
    if (start == null) continue;
    const end = minutes(g.scheduled_end_time) ?? start;
    const game: DayGame = {
      game_number: g.game_number ?? null,
      bracket_label: g.bracket_label ?? null,
      time: g.scheduled_start_time ?? null,
      court: g.court_name ?? null,
    };
    for (const side of ['a', 'b'] as const) {
      const id = g[`team_${side}_registration_id`];
      if (!id) continue; // an unfilled bracket slot is not a team playing
      out.push({
        team: g[`team_${side}_name`] || 'Team',
        division: g.division ?? 'Unassigned',
        date: g.scheduled_date,
        start,
        end,
        game,
      });
    }
  }
  return out;
}

const byTeamDay = (list: Appearance[]) => {
  const m = new Map<string, Appearance[]>();
  for (const a of list) {
    const k = `${a.team}|${a.division}|${a.date}`;
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(a);
  }
  for (const v of m.values()) v.sort((x, y) => x.start - y.start);
  return m;
};

/** Every team over its games-per-day limit, one row per team per day. */
export function overLimitRows(games: any[], limits: ComplianceLimits | null): OverLimitRow[] {
  const rows: OverLimitRow[] = [];
  for (const list of byTeamDay(appearances(games)).values()) {
    const { maxPerDay } = limitsFor(limits, list[0].division);
    // The cap comparison has one home — src/lib/dailyCap.ts — so this report
    // can never disagree with the grid about who is over.
    if (!isOverDailyCap(list.length, maxPerDay)) continue;
    rows.push({
      team: list[0].team,
      division: list[0].division,
      date: list[0].date,
      games: list.length,
      limit: Number(maxPerDay),
      rows: list.map((a) => a.game),
    });
  }
  return rows.sort(
    (a, b) => b.games - a.games || a.date.localeCompare(b.date) ||
      a.division.localeCompare(b.division) || a.team.localeCompare(b.team),
  );
}

/** Every pair of a team's games closer together than the minimum gap. */
export function backToBackRows(games: any[], limits: ComplianceLimits | null): BackToBackRow[] {
  const rows: BackToBackRow[] = [];
  for (const list of byTeamDay(appearances(games)).values()) {
    const { backToBack } = limitsFor(limits, list[0].division);
    if (!backToBack || backToBack <= 0) continue;
    for (let i = 1; i < list.length; i++) {
      const gap = list[i].start - list[i - 1].end;
      if (gap <= backToBack) {
        rows.push({
          team: list[i].team,
          division: list[i].division,
          date: list[i].date,
          gap_minutes: gap,
          minimum: backToBack,
          first: list[i - 1].game,
          second: list[i].game,
        });
      }
    }
  }
  return rows.sort(
    (a, b) => a.date.localeCompare(b.date) || a.division.localeCompare(b.division) ||
      a.team.localeCompare(b.team) || a.gap_minutes - b.gap_minutes,
  );
}
